import { useState, useEffect } from 'react';
import { Loader2, Search, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function ProbeRow({ probe, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const isOk = probe.status_code >= 200 && probe.status_code < 300;

  return (
    <>
      <tr
        className="border-b last:border-0 cursor-pointer hover:bg-muted/30"
        onClick={() => setOpen(!open)}
      >
        <td className="px-2 py-1.5 text-xs flex items-center gap-1.5">
          {open ? (
            <ChevronDown className="h-3 w-3 shrink-0" />
          ) : (
            <ChevronRight className="h-3 w-3 shrink-0" />
          )}
          {probe.label}
        </td>
        <td className="px-2 py-1.5 text-xs font-mono">{probe.path}</td>
        <td className="px-2 py-1.5 text-xs">
          <Badge
            variant={isOk ? 'default' : 'destructive'}
            className={isOk ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            {probe.status_code}
          </Badge>
        </td>
        <td className="px-2 py-1.5 text-xs tabular-nums text-right">
          {probe.elapsed_ms != null ? `${probe.elapsed_ms}` : '-'}
        </td>
      </tr>
      {open && (
        <tr className="border-b last:border-0">
          <td colSpan={4} className="px-3 py-2 bg-muted/20">
            <div className="space-y-2 text-xs">
              {probe.top_level_keys && (
                <div>
                  <p className="font-medium text-muted-foreground mb-1">Top-level keys</p>
                  <div className="flex flex-wrap gap-1">
                    {probe.top_level_keys.map((k) => (
                      <Badge key={k} variant="outline" className="text-[10px] font-mono">{k}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {probe.sample_item_keys && (
                <div>
                  <p className="font-medium text-muted-foreground mb-1">Sample item keys</p>
                  <div className="flex flex-wrap gap-1">
                    {probe.sample_item_keys.map((k) => (
                      <Badge key={k} variant="outline" className="text-[10px] font-mono">{k}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {probe.list_key && (
                <div>
                  <p className="font-medium text-muted-foreground mb-1">
                    List key: <span className="font-mono">{probe.list_key}</span>
                    {probe.list_length != null && ` (${probe.list_length} items)`}
                  </p>
                </div>
              )}
              {probe.pagination_shape && (
                <div>
                  <p className="font-medium text-muted-foreground mb-1">Pagination shape</p>
                  <pre className="border rounded p-2 overflow-x-auto bg-muted/30 text-[10px] max-h-32 overflow-y-auto">
                    {JSON.stringify(probe.pagination_shape, null, 2)}
                  </pre>
                </div>
              )}
              {probe.rate_limit_headers && Object.keys(probe.rate_limit_headers).length > 0 && (
                <div>
                  <p className="font-medium text-muted-foreground mb-1">Rate limit headers</p>
                  <pre className="border rounded p-2 overflow-x-auto bg-muted/30 text-[10px] max-h-32 overflow-y-auto">
                    {JSON.stringify(probe.rate_limit_headers, null, 2)}
                  </pre>
                </div>
              )}
              {probe.sample_item && (
                <details className="text-[10px]">
                  <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
                    Sample item
                  </summary>
                  <pre className="mt-1 border rounded p-2 overflow-x-auto bg-muted/30 max-h-48 overflow-y-auto">
                    {JSON.stringify(probe.sample_item, null, 2)}
                  </pre>
                </details>
              )}
              {probe.body && !probe.sample_item && (
                <details className="text-[10px]">
                  <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
                    Response body
                  </summary>
                  <pre className="mt-1 border rounded p-2 overflow-x-auto bg-muted/30 max-h-48 overflow-y-auto">
                    {JSON.stringify(probe.body, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function PennylaneConnectionPanel() {
  const [entityId, setEntityId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Seed entity form state
  const [seedOpen, setSeedOpen] = useState(false);
  const [seedLoading, setSeedLoading] = useState(false);
  const [seedForm, setSeedForm] = useState({
    entity_id: '',
    entity_name: '',
    api_key_env_var: '',
    country: '',
    notes: '',
  });

  // Pennylane-entity options for the seed form, sourced from the same
  // registry-backed endpoint PennylaneConnectionTestPanel uses. Was a
  // free-text input: a typo'd entity_id here made every later connection
  // check for the correct slug come back "no credential" (empty-but-200,
  // indistinguishable from a real missing credential).
  const [pennylaneEntities, setPennylaneEntities] = useState([]);

  useEffect(() => {
    diagnosticsAPI.pennylaneEntities()
      .then(({ data }) => setPennylaneEntities(data.entities || []))
      .catch((err) => toast.error(getErrorMessage(err)));
  }, []);

  // Stamping invariants: diagnose-before-fixing instrument for the
  // suspicion that uplisting_reservations.entity_id may be mis-stamped the
  // same way CLAUDE.md section 5 flags for bank_accounts/transactions.
  // invoicing_entity_owner_filter and generate_drafts match by raw
  // entity_id with no fallback -- if this collection were mis-stamped,
  // invoice generation would silently zero out rather than error.
  const [invariantsOpen, setInvariantsOpen] = useState(false);
  const [invariantsLoading, setInvariantsLoading] = useState(false);
  const [invariants, setInvariants] = useState(null);

  const loadInvariants = async () => {
    setInvariantsLoading(true);
    try {
      const { data } = await diagnosticsAPI.uplistingStampingInvariants();
      setInvariants(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setInvariantsLoading(false);
    }
  };

  const runProbe = async () => {
    const id = entityId.trim();
    if (!id) {
      toast.error('Enter an entity ID');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.pennylaneConnectionCheck(id);
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') runProbe();
  };

  const updateSeedField = (field, value) => {
    setSeedForm((prev) => ({ ...prev, [field]: value }));
  };

  const runSeed = async () => {
    if (!seedForm.entity_id.trim() || !seedForm.entity_name.trim() || !seedForm.api_key_env_var.trim()) {
      toast.error('entity_id, entity_name, and api_key_env_var are required');
      return;
    }
    setSeedLoading(true);
    try {
      const { data } = await diagnosticsAPI.pennylaneSeedEntity(seedForm);
      toast.success(data.message || `Entity ${data.entity_id} ${data.status}`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSeedLoading(false);
    }
  };

  const authOk = result?.auth_status === 'ok';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Pennylane Connection Check</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Probe the Pennylane API for a given entity. Tests authentication, then
          fetches one record from each endpoint to confirm shapes.
        </p>

        <div className="flex items-center gap-3">
          <input
            type="text"
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Entity slug, e.g. bonjour-residences-sas"
            className="border rounded px-3 py-1.5 text-sm w-72 bg-background"
          />
          <Button onClick={runProbe} disabled={loading || !entityId.trim()}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Probe
          </Button>
        </div>

        {result && (
          <div className="space-y-4">
            {/* Auth status + metadata */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1.5">
                {authOk ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                )}
                <Badge variant={authOk ? 'default' : 'destructive'}
                       className={authOk ? 'bg-green-600 hover:bg-green-700' : ''}>
                  {result.auth_status}
                </Badge>
              </div>
              {result.entity_name && (
                <span className="text-sm font-medium">{result.entity_name}</span>
              )}
              {result.checked_at && (
                <span className="text-xs text-muted-foreground ml-auto">
                  {new Date(result.checked_at).toLocaleString()}
                </span>
              )}
            </div>

            {result.api_base_url && (
              <p className="text-xs text-muted-foreground font-mono">
                {result.api_base_url}
              </p>
            )}

            {/* Probe results table */}
            {result.probes && result.probes.length > 0 && (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1.5 font-medium">Label</th>
                      <th className="text-left px-2 py-1.5 font-medium">Path</th>
                      <th className="text-left px-2 py-1.5 font-medium">Status</th>
                      <th className="text-right px-2 py-1.5 font-medium">Time (ms)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.probes.map((probe, i) => (
                      <ProbeRow key={probe.path || i} probe={probe} defaultOpen={false} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Seed Entity section */}
        <div className="border-t pt-4">
          <button
            type="button"
            onClick={() => setSeedOpen(!seedOpen)}
            className="flex items-center gap-2 text-sm font-medium hover:text-foreground text-muted-foreground"
          >
            {seedOpen ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
            Seed Entity
          </button>

          {seedOpen && (
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-1">
                    entity_id
                  </label>
                  <select
                    value={seedForm.entity_id}
                    onChange={(e) => {
                      const id = e.target.value;
                      const match = pennylaneEntities.find((ent) => ent.entity_id === id);
                      setSeedForm((prev) => ({
                        ...prev,
                        entity_id: id,
                        entity_name: match ? match.canonical_name : prev.entity_name,
                      }));
                    }}
                    className="h-9 rounded-md border border-input bg-background px-3 text-sm w-full"
                  >
                    <option value="">Select a Pennylane entity...</option>
                    {pennylaneEntities.map((ent) => (
                      <option key={ent.entity_id} value={ent.entity_id}>
                        {ent.canonical_name} ({ent.entity_id})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-1">
                    entity_name
                  </label>
                  <input
                    type="text"
                    value={seedForm.entity_name}
                    onChange={(e) => updateSeedField('entity_name', e.target.value)}
                    placeholder="Bonjour Residences SAS"
                    className="h-9 rounded-md border border-input bg-background px-3 text-sm w-full"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-1">
                    api_key_env_var
                  </label>
                  <input
                    type="text"
                    value={seedForm.api_key_env_var}
                    onChange={(e) => updateSeedField('api_key_env_var', e.target.value)}
                    placeholder="PENNYLANE_API_KEY_BR_SAS"
                    className="h-9 rounded-md border border-input bg-background px-3 text-sm w-full"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-1">
                    country
                  </label>
                  <input
                    type="text"
                    value={seedForm.country}
                    onChange={(e) => updateSeedField('country', e.target.value)}
                    placeholder="FR"
                    className="h-9 rounded-md border border-input bg-background px-3 text-sm w-full"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">
                  notes
                </label>
                <input
                  type="text"
                  value={seedForm.notes}
                  onChange={(e) => updateSeedField('notes', e.target.value)}
                  placeholder="Optional notes"
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm w-full"
                />
              </div>
              <Button onClick={runSeed} disabled={seedLoading}>
                {seedLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Seed
              </Button>
            </div>
          )}
        </div>

        {/* Stamping Invariants section */}
        <div className="border-t pt-4">
          <button
            type="button"
            onClick={() => {
              const next = !invariantsOpen;
              setInvariantsOpen(next);
              if (next && !invariants) loadInvariants();
            }}
            className="flex items-center gap-2 text-sm font-medium hover:text-foreground text-muted-foreground"
          >
            {invariantsOpen ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
            Stamping Invariants (uplisting_reservations)
          </button>

          {invariantsOpen && (
            <div className="mt-3 space-y-3">
              <p className="text-xs text-muted-foreground">
                Read-only check of whether uplisting_reservations.entity_id is
                mis-stamped the way CLAUDE.md section 5 flags as an open remediation
                for bank_accounts/transactions. Invoice generation matches this
                collection by raw entity_id with no fallback; if it were mis-stamped,
                generation would silently produce zero candidates instead of erroring.
              </p>

              {invariantsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Scanning...
                </div>
              ) : invariants ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    {invariants.all_clear ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                    )}
                    <span className="text-sm font-medium">
                      {invariants.all_clear
                        ? 'All clear -- the raw entity_id match is safe.'
                        : 'Mis-stamped documents found -- see counts below.'}
                    </span>
                    <span className="text-xs text-muted-foreground ml-auto">
                      Scanned {invariants.scanned} / {invariants.total_reservations}
                      {invariants.scan_truncated ? ' (truncated)' : ''}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      { key: 'entity_id_absent', label: 'entity_id absent' },
                      { key: 'entity_id_not_in_registry', label: 'entity_id not in registry' },
                      { key: 'entity_id_uuid_mismatch', label: 'entity_id/entity_uuid mismatch' },
                    ].map(({ key, label }) => {
                      const bucket = invariants[key];
                      const bad = bucket.count > 0;
                      return (
                        <div key={key} className={`rounded border px-3 py-2 ${bad ? 'bg-red-50/50 border-red-200' : 'bg-muted/20'}`}>
                          <p className="text-xs font-medium text-muted-foreground mb-1">{label}</p>
                          <p className={`text-lg font-semibold ${bad ? 'text-red-700' : 'text-green-700'}`}>
                            {bucket.count}
                          </p>
                          {bad && bucket.sample.length > 0 && (
                            <details className="text-[10px] mt-1">
                              <summary className="cursor-pointer text-muted-foreground">
                                Sample ({bucket.sample.length})
                              </summary>
                              <pre className="mt-1 border rounded p-2 overflow-x-auto bg-muted/30 max-h-32 overflow-y-auto">
                                {JSON.stringify(bucket.sample, null, 2)}
                              </pre>
                            </details>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
